import { defineStore } from 'pinia'
import { reactive } from 'vue'
import { ARTICLE, REPORT } from '@/_core/endpoint'
import apiRequestManager from '@/_core/apiRequestManager'
import TOAST_MESSAGE_TYPES from '@/constants/toastMessageTypes'
import { useToastStore } from '@/stores/toast'

/**
 * @description なぜブックマークだけ store 化しているのか（ブックマークボタンコンポーネント内で直接 API 処理を持たせなかったのか）について
 *
 * レポート記事のブックマーク機能なので、本来はレポートページ側の Main.vue にブックマークの「状態（データ）」を持たせたかったが、
 * ブックマークの総数取得が独立した API ではなく、コメント一覧取得 API のレスポンスの中に紛れ込んでいるという API 設計であったため、
 * もし仮にレポートページの Main.vue 側に「状態」を持たせてしまった場合、
 * レポートページの AsidePrimary 内でコメント一覧取得 API をつついた後、
 * ブックマーク数だけ App に emit し、App がそれを Main に伝え、Main の状態を更新するというバケツリレーが発生してしまうため、
 * そのあたりのデータフローを簡略化するためにブックマークを store 化し、コメント一覧取得 API のコールバック内で直接ブックマーク情報を更新するという構造にした。
 *
 * この「ブックマーク情報の供給元がコメント一覧取得 API だけ」という構造の帰結として、
 * コメント欄が描画されない条件（アプリ・コメント投稿用プロフィールの異常系）ではブックマーク情報が取得されないため、
 * ブックマークボタンの表示条件もコメント欄の有無（useComment の hasCommentSection）に揃えている。
 * 本来はブックマーク件数を独立した API で取得する（あるいは view_props で供給する）のが筋であり、
 * ブックマークをコメント欄と独立に表示したい要件が出た時点でこの構造は破綻するため、その時に見直すこと。
 */
export const useBookmarkStore = defineStore('bookmark', () => {
  // store
  const toastStore = useToastStore()

  interface BookmarkStoreContext {
    isActive: boolean,

    /**
     * 総ブックマーク数
     * @description 未取得を 0 件と偽らないため、取得前は undefined とする
     *              （消費側が「（0）」と表示してしまうのを、呼び出しごとの出し分けではなくデータ側で防ぐ）
     */
    total: number | undefined,

    /**
     * ブックマーク情報を取得済みかどうか
     * @description 上述のとおり、ブックマーク情報はコメント一覧取得 API のレスポンスから供給される。
     *              取得前は isActive / total が初期値のままであり、その状態で操作を許してしまうと、
     *              サーバ側はトグルで解除するのにフロントは「ブックマークした」と表示してしまうため（実状態と逆になる）、
     *              取得が完了するまで操作を受け付けないためのフラグとして持つ
     */
    isReady: boolean,
  }

  const context: BookmarkStoreContext = reactive({
    isActive: false,
    total: undefined,
    isReady: false,
  })

  const createBookmarkEndpoint = (contentType: BookmarkContentType, contentId: number) => {
    const baseEndpoint = contentType === 'article'
      ? ARTICLE.BOOKMARK
      : REPORT.BOOKMARK
    const path = baseEndpoint.path({
      id: contentId,
    })

    return { ...baseEndpoint, path }
  }

  /**
   * リクエスト処理中かどうか
   * @description ブックマークの endpoint は screenLock を無効にしている（_core/endpoint.ts）ため、
   *              フォローボタン（components/follow/ButtonFollow.vue）と異なり画面遮蔽による連打抑止が働かず、連打がそのまま届く。
   *              ブックマーク API は1リクエストごとのトグルであり、多重発行するとサーバ側は元の状態に戻る一方、
   *              成功 callback ごとにローカルの状態を反転してしまい表示と実状態が食い違うため、このフラグで抑止する
   */
  let inFlight = false

  /**
   * 記事のブックマーク
   * @param contentType 記事種別
   * @param contentId 記事 ID
   */
  const handleBookmark = async (contentType: BookmarkContentType, contentId: number) => {
    if (inFlight) return

    // 未取得の状態で操作すると実状態と逆の表示・トーストになるため、取得が完了するまでは受け付けない
    if (!context.isReady) return

    const endpoint = createBookmarkEndpoint(contentType, contentId)

    try {
      inFlight = true

      await apiRequestManager.handleRequest({
        endpoint,
        callback: (response: null, isSuccess: boolean) => {
          if (isSuccess) {
            context.isActive = !context.isActive

            // 上の isReady ガードにより未取得では到達しないが、callback 内には narrowing が届かないため改めて判定する。
            // 件数の更新だけを条件下に置いているのは、ここで早期 return すると
            // サーバ側はトグル済みなのに状態の反転もトーストも行われないという最も悪い劣化になるため
            if (context.total !== undefined) {
              context.total = context.isActive
                ? context.total + 1
                : context.total - 1
            }
          }

          handleToastMessage(isSuccess, context.isActive)
        }
      })
    } catch (error) {
      console.error(error)
      handleToastMessage(false, false)
    } finally {
      inFlight = false
    }
  }

  /**
   * ブックマーク情報の設定
   * @param isActive 自身がブックマークしているかどうか
   * @param total 総ブックマーク数
   * @description 供給元はコメント一覧取得 API のみであり、isReady を立てる地点を1箇所に閉じるため、まとめて設定する
   * @description context.total は未取得を表す undefined を含むが、供給元は必ず件数を返すため、引数では number に絞る
   */
  const setBookmark = ({ isActive, total }: { isActive: boolean, total: number }) => {
    context.isActive = isActive
    context.total = total
    context.isReady = true
  }

  function handleToastMessage(isSuccess: boolean, isActive: boolean) {
    const id = `toast-${new Date().getTime()}`
    const messageType: MessageType = isSuccess
      ? TOAST_MESSAGE_TYPES.SUCCESS as MessageType
      : TOAST_MESSAGE_TYPES.ALERT as MessageType

    const messageSuffix = isActive
      ? 'しました。'
      : '解除しました。'

    const message = isSuccess
      ? `ブックマーク${messageSuffix}`
      : 'ブックマーク状態の更新に失敗しました。'

    toastStore.setToastItem({
      id,
      messageType,
      message,
      duration: 4,
    })
  }

  return {
    // data
    context,

    // methods
    handleBookmark,
    setBookmark,
  }
})
